import {ChangeDetectionStrategy, Component, computed, input} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AggregertResultatModell, SpørreundersøkelseId, SpørsmålModell, Spørsmålstype} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TranslateModule} from "@ngx-translate/core";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {RangeringAggregatOgTittel, RangeringResultaterSøylediagramKomponent, SøylediagramOppsett} from "../rangering-resultater-søylediagram/rangering-resultater-søylediagram";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('SpørreundersøkelseSpørsmålKorellertResultatKomponent');

/**
 * Viser resultat for et spørsmål aggregert på undersøkelsesnivå.
 */
@Component({
    selector: 'spoerreundersoekelse-spoersmaal-korellert-resultat',
    templateUrl: 'spørreundersøkelse-spørsmål-korellert-resultat.html',
    styleUrl: 'spørreundersøkelse-spørsmål-korellert-resultat.scss',
    host: {
        '[class]': '["spørsmålstype-"+ spoersmaalstyper[resultat().spoersmaal.type], "visualiseringstype-"+ spoersmaalstyper[oppsett().visualiseringstype], "valgtittel-"+oppsett()?.valgtittelPlassering]',
        '[style.--diagramhøyde-px.px]': 'diagramoppsett()?.diagramHoeydePx'
    },
    imports: [
        TranslateModule,
        HtmlFormatertKomponent,
        RangeringResultaterSøylediagramKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelseSpørsmålKorellertResultatKomponent {

    /** Spørsmål med resultater per undersøkelse, forventer at {@link SpørsmålOgResultater#resultater} inneholder minst ett innslag. */
    resultat = input.required<SpørsmålOgResultater>();
    oppsett = input.required<ResultatOppsett>();

    resultatPrimaerUndersoekelse = computed<UndersøkelseOgResultat>(() => this.resultat().resultater[0]);
    diagramoppsett = computed<SøylediagramOppsett | null>(() => this.beregnDiagramOppsett());
    spoersmaalstyper = Spørsmålstype;

    constructor() {
    }

    /** Returnerer 'ja' svar i {@link #resultat}.
     * Forutsetter at {@link ResultatOppsett#visualiseringstype} er 'boolsk' */
    boolskAntallJa(): number {
        return this.resultat().resultater.reduce((sum, resultat) => {
            return resultat.resultat!.boolsk ? sum + 1 : sum;
        }, 0);
    }

    /**  Returnerer 'nei' svar i {@link #resultat}.
     * Forutsetter at {@link ResultatOppsett#visualiseringstype} er 'boolsk' */
    boolskAntallNei(): number {
        return this.resultat().resultater.reduce((sum, resultat) => {
            return resultat.resultat!.boolsk ? sum + 1 : sum;
        }, 0);
    }

    /** Returnerer {@link #resultat} som en samling av {@link RangeringAggregatOgTittel}.
     * Forutsetter at {@link ResultatOppsett#visualiseringstype} er 'rangering' */
    rangeringSomRangeringAggregatOgTittel(): RangeringAggregatOgTittel[] {
        return this.resultat().resultater
          .map(resultat => ({
              id: resultat.undersoekelseId,
              tittel: resultat.undersoekelseTittel,
              tittelKort: resultat.undersoekelseTittelKort,
              aggregat: resultat.resultat!.rangering! //forutsetter at komponenten ikke blir brukt dersom det ikke finnes noen resultater
          }));
    }

    /** Returnerer {@link #resultat} som en samling av {@link RangeringAggregatOgTittel}.
     * Forutsetter at {@link ResultatOppsett#visualiseringstype} er 'velgEnAvFlere' */
    velgEnAvFlereSomRangeringAggregatOgTittel(): RangeringAggregatOgTittel[] {
        return this.resultat().resultater
          .map(resultat => ({
              id: resultat.undersoekelseId,
              tittel: resultat.undersoekelseTittel,
              tittelKort: resultat.undersoekelseTittelKort,
              aggregat: resultat.resultat!.velgEnAvFlere! //forutsetter at komponenten ikke blir brukt dersom det ikke finnes noen resultater
          }));
    }

    /** Returnerer {@link #resultat} som en samling av {@link RangeringAggregatOgTittel}.
     * Forutsetter at {@link ResultatOppsett#visualiseringstype} er 'velgNoenAvFlere' */
    velgNoenAvFlereSomRangeringAggregatOgTittel(): RangeringAggregatOgTittel[] {
        return this.resultat().resultater
          .map(resultat => ({
              id: resultat.undersoekelseId,
              tittel: resultat.undersoekelseTittel,
              tittelKort: resultat.undersoekelseTittelKort,
              aggregat: resultat.resultat!.velgNoenAvFlere! //forutsetter at komponenten ikke blir brukt dersom det ikke finnes noen resultater
          }));
    }

    //private hjelpemetoder

    /** Beregner {@link SøylediagramOppsett} for {@link #resultat}.
     * Returnerer null dersom diagram ikke benyttes for aktuell spørsmålstype.
     * Spørsmålstype.grupperingstittel får spesialbehandling da vi for denne viser diagram med aggregerte verdier for spørsmålene i gruppen.
     * For grupper med spørsmål av typen velgNoenAvFlere benyttes stående søyler, da andelene kan summere til mer enn 100% og dermed ikke kan vises som én liggende kombinert søyle */
    private beregnDiagramOppsett(): SøylediagramOppsett | null {
        switch (this.resultat().spoersmaal.type) {
            case Spørsmålstype.grupperingstittel:
                if (this.oppsett().visualiseringstype === Spørsmålstype.velgNoenAvFlere)
                    return {diagramHoeydePx: 100, format: 'staaende', ...this.oppsett()};
                const antallUndersøkelser = this.resultat().resultater.length;
                /** første (dominerende) rad skal være høyere den de øvrige, se {@link RangeringResultaterSøylediagramKomponent#beregnLiggendeKombinertData}
                 * for hvordan diagramHoeydePx omsettes til faktisk høyde for 'barene' som representerer de ulike undersøkelsene */
                return {...this.oppsett(), diagramHoeydePx: 50 + ((antallUndersøkelser - 1) * 20), format: 'liggendeKombinert'};
            case Spørsmålstype.rangering:
            case Spørsmålstype.velgEnAvFlere:
            case Spørsmålstype.velgNoenAvFlere:
                return {diagramHoeydePx: 100, format: 'staaende', ...this.oppsett()};
            default:
                return null
        }
    }

    protected readonly erIkkeNil = erIkkeNil;
}

export type ResultatOppsett = SøylediagramOppsett & {
    /** Angir hvordan resultatene skal vises.
     * Normalt vil dette være det samme som typen i {@link SpørsmålOgResultater#spoersmaal},
     * men for spørsmål av typen 'grupperingstittel' vil 'visualiseringstype' typisk være satt til typen som alle spørsmålene i gruppen som denne grupperingstitlen representerer */
    visualiseringstype: Spørsmålstype;
    /** Dersom usann vises ikke svarverdier */
    visResultat?: boolean;
}

export interface SpørsmålOgResultater {
    get spoersmaal(): SpørsmålModell;
    /** Liste med ett innslag per spørreundersøkelse  */
    get resultater(): UndersøkelseOgResultat[];
}

export type UndersøkelseOgResultat = {
    undersoekelseId: SpørreundersøkelseId;
    /** Fulle tittelen til undersøkelsen */
    undersoekelseTittel: string;
    /** Tittel for dette aggregatet, brukes for å synliggjøre forskjellen mellom UndersøkelseOgResultat-innslag dersom flere er angitt i {@link RangeringResultaterSøylediagramKomponent#resultater}.
     * Merk at denne kun vises dersom {@link RangeringResultaterSøylediagramKomponent#resultater} inneholder mer enn ett innslag */
    undersoekelseTittelKort: string;
    /** Aggregert resultat for et spørsmål.
     * Null dersom det ikke finnes responser for dette spørsmålet i aktuell spørreundersøkelse */
    resultat: AggregertResultatModell | null;
}
